/**
 * Creating, renaming and removing a table column loop (`{{#columns months}}`, CDM v35) in the editor.
 *
 * A column loop is the `columnLoop` attribute on a run of adjacent cells in one row: the grid columns those cells cover
 * repeat once per item, in every row (resolved before rendering). The run is the maximal stretch of neighbouring cells
 * in that row carrying an equal loop. Creating one marks the selected cells of one row (or the cursor's cell); it may
 * not overlap another loop. Renaming rewrites every cell of the run; removing clears them. Each is one transaction.
 */
import type { Editor } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { CellSelection } from '@tiptap/pm/tables'
import { closeHistory } from '@tiptap/pm/history'
import { DEFAULT_ITEM_ALIAS, itemAliasError, loopNormalizedKey, loopSourceError } from './loopRunEdit'
import type { TableColumnLoop } from './types'

const isCell = (n: PMNode) => n.type.name === 'tableCell' || n.type.name === 'tableHeader'
const sameLoop = (a: TableColumnLoop | null | undefined, b: TableColumnLoop | null | undefined) =>
  !!a && !!b && a.rawName === b.rawName && a.normalizedKey === b.normalizedKey && a.itemAlias === b.itemAlias

/** Where the edit applies: the row, the cells it would mark, and the loop already there (if any). */
export interface ColumnLoopTarget {
  /** Absolute positions of the cells to mark (or already marked), left to right. */
  cells: number[]
  existing: TableColumnLoop | null
  /** Why no loop can be made here, or null. */
  error: string | null
}

function rowCells(row: PMNode, rowPos: number): { pos: number; node: PMNode }[] {
  const out: { pos: number; node: PMNode }[] = []
  row.forEach((cell, offset) => { if (isCell(cell)) out.push({ pos: rowPos + 1 + offset, node: cell }) })
  return out
}

export function columnLoopTarget(editor: Editor): ColumnLoopTarget | null {
  const { state } = editor
  const sel = state.selection
  let rowPos = -1
  let row: PMNode | null = null
  let picked: number[] = []

  if (sel instanceof CellSelection) {
    const rows = new Set<number>()
    sel.forEachCell((_n, pos) => { picked.push(pos); rows.add(state.doc.resolve(pos).before()) })
    if (rows.size !== 1) return { cells: [], existing: null, error: 'Select cells in one row: their columns repeat in every row.' }
    rowPos = [...rows][0]
    row = state.doc.nodeAt(rowPos)
  } else {
    const $from = sel.$from
    for (let d = $from.depth; d > 0; d--) {
      if (isCell($from.node(d))) {
        picked = [$from.before(d)]
        rowPos = $from.before(d - 1)
        row = $from.node(d - 1)
        break
      }
    }
  }
  if (!row || rowPos < 0 || picked.length === 0) return null

  const cells = rowCells(row, rowPos)
  picked.sort((a, b) => a - b)
  const loops = picked.map(p => (state.doc.nodeAt(p)?.attrs.columnLoop as TableColumnLoop | null) ?? null)

  // On a loop: the whole run of cells carrying it.
  const hit = loops.find(l => l)
  if (hit) {
    if (!loops.every(l => sameLoop(l, hit)) && !(picked.length === 1)) {
      return { cells: [], existing: null, error: 'The selection covers cells outside this column loop.' }
    }
    const start = cells.findIndex(c => c.pos === picked[0])
    let a = start, b = start
    while (a > 0 && sameLoop(cells[a - 1].node.attrs.columnLoop, hit)) a--
    while (b < cells.length - 1 && sameLoop(cells[b + 1].node.attrs.columnLoop, hit)) b++
    return { cells: cells.slice(a, b + 1).map(c => c.pos), existing: hit, error: null }
  }

  // A new loop: the picked cells must be neighbours.
  const idx = picked.map(p => cells.findIndex(c => c.pos === p))
  if (idx.some((i, k) => i < 0 || (k > 0 && i !== idx[k - 1] + 1))) {
    return { cells: [], existing: null, error: 'Select neighbouring cells in one row.' }
  }
  return { cells: picked, existing: null, error: null }
}

export interface ColumnLoopEdit {
  rawName: string
  itemAlias: string
}

export function columnLoopEditError(edit: ColumnLoopEdit): string | null {
  return loopSourceError(edit.rawName) ?? itemAliasError(edit.itemAlias)
}

/** Marks (or re-marks) the target's cells with the loop; null removes it. */
export function applyColumnLoop(editor: Editor, target: ColumnLoopTarget, edit: ColumnLoopEdit | null): boolean {
  if (target.error || target.cells.length === 0) return false
  if (edit && columnLoopEditError(edit)) return false
  const rawName = edit?.rawName.trim() ?? ''
  const loop: TableColumnLoop | null = edit
    ? { rawName, normalizedKey: loopNormalizedKey(rawName), itemAlias: edit.itemAlias.trim() || DEFAULT_ITEM_ALIAS }
    : null
  const { state, view } = editor
  const tr = closeHistory(state.tr)
  for (const pos of target.cells) {
    const node = tr.doc.nodeAt(pos)
    if (!node || !isCell(node)) return false
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, columnLoop: loop })
  }
  view.dispatch(tr)
  return true
}
